"use client";

import { HeroSection } from "@/components/sections/hero";
import { ExecutiveSummarySection } from "@/components/sections/executive-summary";
import { ProblemSection } from "@/components/sections/problem";
import { SolutionSection } from "@/components/sections/solution";
import { CapabilityOverviewSection } from "@/components/sections/capability-overview";
import { HowItWorksSection } from "@/components/sections/how-it-works";
import { CoreFeaturesSection } from "@/components/sections/core-features";
import { AdvancedFeaturesSection } from "@/components/sections/advanced-features";
import { UseCasesSection } from "@/components/sections/use-cases";
import { BenefitsSection } from "@/components/sections/benefits";
import { UspsSection } from "@/components/sections/usps";
import { WhoItsForSection } from "@/components/sections/who-its-for";
import { WhyChooseUsSection } from "@/components/sections/why-choose-us";
import { RelatedSection } from "@/components/sections/related";
import { FAQSection } from "@/components/sections/faq";
import { PAASection } from "@/components/sections/paa";
import { CtaSection } from "@/components/sections/cta";
import { DomainsSection } from "@/components/sections/domains";
import { MappingSection } from "@/components/sections/mapping";
import { TwoListSection } from "@/components/sections/two-list";

import type { LucideIcon } from "lucide-react";

export interface PageData {
  hero: {
    eyebrow: string;
    title: string;
    titleAccent: string;
    lede: string;
    trustLine?: string;
    ctaPrimary: { label: string; href: string };
    ctaSecondary?: { label: string; href: string };
    trustIndicators?: string[];
    introBadges?: string[];
    primaryAccent?: string;
    secondaryAccent?: string;
  };
  executiveSummary: {
    eyebrow: string;
    title: string;
    intro: string;
    paragraphs: string[];
    metrics?: { value: string; label: string; icon: LucideIcon; accent: string }[];
    cta: { label: string; href: string };
  };
  problem: {
    eyebrow: string;
    title: string;
    lede: string;
    accent: string;
    pillars: { title: string; body: string; icon: LucideIcon; accent: string }[];
  };
  solution: {
    eyebrow: string;
    title: string;
    intro: string;
    accent: string;
    pillarAccents: string[];
    pillars: { title: string; body: string; icon: LucideIcon }[];
    cta: { label: string; href: string };
  };
  capabilityOverview?: {
    eyebrow: string;
    title: string;
    intro: string;
    primaryAccent?: string;
    secondaryAccent?: string;
    tertiaryAccent?: string;
    frameworks?: { name: string; description: string; icon?: LucideIcon; accent?: string }[];
    biasLevels?: { title: string; description: string }[];
    variantDimensions?: { label: string; description: string }[];
    localization?: string[];
    columns?: { name: string; description: string; icon: LucideIcon; accent: string }[];
  };
  howItWorks: {
    eyebrow: string;
    title: string;
    accent: string;
    stepAccents: string[];
    steps: { number: string; title: string; body: string }[];
  };
  coreFeatures: {
    eyebrow: string;
    title: string;
    accent: string;
    featureAccents: string[];
    features: { number: string; title: string; body: string; icon?: LucideIcon }[];
  };
  advancedFeatures?: {
    eyebrow: string;
    title: string;
    sectionAccent: string;
    features: { title: string; body: string; icon: LucideIcon; accent: string }[];
  };
  domains?: {
    eyebrow: string;
    title: string;
    intro: string;
    accent: string;
    domains: { name: string; items: string[]; icon: LucideIcon; accent: string }[];
  };
  mapping?: {
    eyebrow: string;
    title: string;
    icon: LucideIcon;
    accent: string;
    levels: { level: string; description: string; accent: string }[];
  };
  twoList?: {
    eyebrow: string;
    title: string;
    intro: string;
    left: { title: string; icon: LucideIcon; accent: string; items: { name: string; description: string }[] };
    right: { title: string; icon: LucideIcon; accent: string; items: { name: string; description: string }[] };
  };
  useCases: {
    eyebrow: string;
    title: string;
    accent: string;
    cases: { title: string; body: string; metric: string; icon: LucideIcon; accent: string }[];
  };
  benefits: {
    eyebrow: string;
    title: string;
    accent: string;
    gridCols?: 2 | 3 | 4;
    benefits: { title: string; body: string; icon?: LucideIcon; accent: string }[];
  };
  usps: {
    eyebrow: string;
    title: string;
    intro?: string;
    accent: string;
    gridCols?: 2 | 3;
    usps: { title: string; body: string; icon: LucideIcon; accent: string }[];
    cta: { label: string; href: string };
  };
  whoItsFor: {
    eyebrow: string;
    title: string;
    accent: string;
    gridCols?: 2 | 3;
    segments: { title: string; body: string; icon: LucideIcon; accent: string }[];
  };
  whyChooseUs: {
    eyebrow: string;
    title: string;
    accent: string;
    gridCols?: 2 | 3;
    reasons: { title: string; body: string; icon: LucideIcon; accent: string }[];
  };
  related: {
    eyebrow: string;
    title: string;
    accent: string;
    capabilities: { name: string; href: string; body: string; icon: LucideIcon; accent: string }[];
    engines: { name: string; href: string; body: string; icon: LucideIcon; accent: string }[];
    services: { name: string; href: string; body: string; icon: LucideIcon; accent: string }[];
  };
  faq: {
    eyebrow: string;
    title: string;
    accent: string;
    items: { q: string; a: string }[];
  };
  paa: {
    eyebrow: string;
    title: string;
    accent: string;
    items: { q: string; a: string }[];
  };
  cta: {
    eyebrow: string;
    title: string;
    intro: string;
    promises: string[];
    ctaPrimary: { label: string; href: string };
    ctaSecondary?: { label: string; href: string };
    ctaTertiary?: { label: string; href: string };
    trustBar: string[];
    accent: string;
    secondaryAccent?: string;
  };
}

export interface CapabilityPageProps {
  data: PageData;
}

export function CapabilityPage({ data }: CapabilityPageProps) {
  const d = data;
  return (
    <>
      <HeroSection
        eyebrow={d.hero.eyebrow}
        title={d.hero.title}
        titleAccent={d.hero.titleAccent}
        lede={d.hero.lede}
        trustLine={d.hero.trustLine}
        ctaPrimary={d.hero.ctaPrimary}
        ctaSecondary={d.hero.ctaSecondary}
        trustIndicators={d.hero.trustIndicators}
        introBadges={d.hero.introBadges}
        primaryAccent={d.hero.primaryAccent}
        secondaryAccent={d.hero.secondaryAccent}
      />
      <div className="bg-aurora-purple">
        <ExecutiveSummarySection
          eyebrow={d.executiveSummary.eyebrow}
          title={d.executiveSummary.title}
          intro={d.executiveSummary.intro}
          paragraphs={d.executiveSummary.paragraphs}
          metrics={d.executiveSummary.metrics}
          cta={d.executiveSummary.cta}
        />
      </div>
      <ProblemSection
        eyebrow={d.problem.eyebrow}
        title={d.problem.title}
        lede={d.problem.lede}
        accent={d.problem.accent}
        pillars={d.problem.pillars}
      />
      <div className="bg-aurora-crimson">
        <SolutionSection
          eyebrow={d.solution.eyebrow}
          title={d.solution.title}
          intro={d.solution.intro}
          accent={d.solution.accent}
          pillarAccents={d.solution.pillarAccents}
          pillars={d.solution.pillars}
          cta={d.solution.cta}
        />
      </div>
      {d.capabilityOverview && (
        <CapabilityOverviewSection
          eyebrow={d.capabilityOverview.eyebrow}
          title={d.capabilityOverview.title}
          intro={d.capabilityOverview.intro}
          primaryAccent={d.capabilityOverview.primaryAccent}
          secondaryAccent={d.capabilityOverview.secondaryAccent}
          tertiaryAccent={d.capabilityOverview.tertiaryAccent}
          frameworks={d.capabilityOverview.frameworks}
          biasLevels={d.capabilityOverview.biasLevels}
          variantDimensions={d.capabilityOverview.variantDimensions}
          localization={d.capabilityOverview.localization}
          columns={d.capabilityOverview.columns}
        />
      )}
      <div className="bg-aurora-emerald">
        <HowItWorksSection
          eyebrow={d.howItWorks.eyebrow}
          title={d.howItWorks.title}
          accent={d.howItWorks.accent}
          stepAccents={d.howItWorks.stepAccents}
          steps={d.howItWorks.steps}
        />
      </div>
      <CoreFeaturesSection
        eyebrow={d.coreFeatures.eyebrow}
        title={d.coreFeatures.title}
        accent={d.coreFeatures.accent}
        featureAccents={d.coreFeatures.featureAccents}
        features={d.coreFeatures.features}
      />
      {d.advancedFeatures && (
        <div className="bg-aurora-violet">
          <AdvancedFeaturesSection
            eyebrow={d.advancedFeatures.eyebrow}
            title={d.advancedFeatures.title}
            sectionAccent={d.advancedFeatures.sectionAccent}
            features={d.advancedFeatures.features}
          />
        </div>
      )}
      {d.domains && (
        <DomainsSection
          eyebrow={d.domains.eyebrow}
          title={d.domains.title}
          intro={d.domains.intro}
          accent={d.domains.accent}
          domains={d.domains.domains}
        />
      )}
      {d.mapping && (
        <div className="bg-aurora-purple">
          <MappingSection
            eyebrow={d.mapping.eyebrow}
            title={d.mapping.title}
            icon={d.mapping.icon}
            accent={d.mapping.accent}
            levels={d.mapping.levels}
          />
        </div>
      )}
      {d.twoList && (
        <TwoListSection
          eyebrow={d.twoList.eyebrow}
          title={d.twoList.title}
          intro={d.twoList.intro}
          left={d.twoList.left}
          right={d.twoList.right}
        />
      )}
      <UseCasesSection
        eyebrow={d.useCases.eyebrow}
        title={d.useCases.title}
        accent={d.useCases.accent}
        cases={d.useCases.cases}
      />
      <div className="bg-aurora-emerald">
        <BenefitsSection
          eyebrow={d.benefits.eyebrow}
          title={d.benefits.title}
          accent={d.benefits.accent}
          gridCols={d.benefits.gridCols}
          benefits={d.benefits.benefits}
        />
      </div>
      <UspsSection
        eyebrow={d.usps.eyebrow}
        title={d.usps.title}
        intro={d.usps.intro}
        accent={d.usps.accent}
        gridCols={d.usps.gridCols}
        usps={d.usps.usps}
        cta={d.usps.cta}
      />
      <div className="bg-aurora-denim">
        <WhoItsForSection
          eyebrow={d.whoItsFor.eyebrow}
          title={d.whoItsFor.title}
          accent={d.whoItsFor.accent}
          gridCols={d.whoItsFor.gridCols}
          segments={d.whoItsFor.segments}
        />
      </div>
      <WhyChooseUsSection
        eyebrow={d.whyChooseUs.eyebrow}
        title={d.whyChooseUs.title}
        accent={d.whyChooseUs.accent}
        gridCols={d.whyChooseUs.gridCols}
        reasons={d.whyChooseUs.reasons}
      />
      <RelatedSection
        eyebrow={d.related.eyebrow}
        title={d.related.title}
        accent={d.related.accent}
        capabilities={d.related.capabilities}
        engines={d.related.engines}
        services={d.related.services}
      />
      <div className="bg-aurora-cyan">
        <FAQSection
          eyebrow={d.faq.eyebrow}
          title={d.faq.title}
          accent={d.faq.accent}
          items={d.faq.items}
        />
      </div>
      <PAASection
        eyebrow={d.paa.eyebrow}
        title={d.paa.title}
        accent={d.paa.accent}
        items={d.paa.items}
      />
      <CtaSection
        eyebrow={d.cta.eyebrow}
        title={d.cta.title}
        intro={d.cta.intro}
        promises={d.cta.promises}
        ctaPrimary={d.cta.ctaPrimary}
        ctaSecondary={d.cta.ctaSecondary}
        ctaTertiary={d.cta.ctaTertiary}
        trustBar={d.cta.trustBar}
        accent={d.cta.accent}
        secondaryAccent={d.cta.secondaryAccent}
      />
    </>
  );
}
